import type { Role } from '@/types';

import type { DealerDeviceAllocation, DealerStatus } from './types';

/** Who may open the Dealers tab (grilling Q3): read routes admit Customer Support too. Acting on a
 *  Dealer is narrower: see `DEALER_MANAGE_ROLES`. */
export const DEALERS_ROLES: Role[] = ['super_admin', 'admin', 'operational_associate'];
/** Access-denied toast name for the User Management hub (ADR-0014). */
export const USER_MANAGEMENT_PAGE_NAME = 'User Management';

/** 118479 §B empty/failure copy (AC wins over Figma's title case, grilling Q4). */
export const DEALERS_EMPTY = 'No dealers.';
export const DEALERS_LOAD_ERROR = "We couldn't load the dealer list. Please try again.";

/** 118479 §A (AC wins over Figma's "Search Dealers", grilling Q4). */
export const DEALERS_SEARCH_PLACEHOLDER = 'Search dealers';
/** 118479 §A: search from the 2nd character, capped at 100 (BE `MIN/MAX_DEALER_SEARCH_LENGTH`). */
export const DEALER_SEARCH_MIN = 2;
export const DEALER_SEARCH_MAX = 100;

/** `<DataTable tableId>` of the Dealers listing; its URL params are `dealers.*`. */
export const DEALERS_TABLE_ID = 'dealers';
/** Search and sort survive a reload; page and page size reset (118479 §H). */
export const DEALERS_RELOAD_RESET_PARAMS = [
  `${DEALERS_TABLE_ID}.page`,
  `${DEALERS_TABLE_ID}.pageSize`,
] as const;

/** 118944 detail copy. `DEALER_UNAVAILABLE` matches the BE's `DEALER_NOT_FOUND` message. */
export const DEALER_UNAVAILABLE =
  'This dealer is no longer available. It may have been removed by another user.';
export const DEALER_LOAD_ERROR = 'This dealer could not be loaded.';
export const DEALER_DEVICES_LOAD_ERROR = "We couldn't load the devices. Please try again.";
export const DEALER_DEVICES_NO_MATCH = 'No devices found.';
/** 118944 §I: each tab's own empty state. */
export const DEALER_DEVICES_EMPTY: Record<DealerDeviceAllocation, string> = {
  unassigned: 'No unassigned devices.',
  self_assigned: 'No self-assigned devices.',
  sold: 'No devices sold yet.',
};

/** 118944 §H export toasts. */
export const DEALER_DEVICES_EXPORTED = 'Devices has been exported successfully.';
export const DEALER_DEVICES_EXPORT_ERROR = "We couldn't export the devices. Please try again.";

export const DEALER_ERROR_CODES = {
  phoneExists: 'USER_PHONE_EXISTS',
  emailExists: 'USER_EMAIL_EXISTS',
  companyNameExists: 'DEALER_COMPANY_NAME_EXISTS',
  invalidCity: 'INVALID_CITY',
  validationFailed: 'VALIDATION_FAILED',
  notFound: 'DEALER_NOT_FOUND',
  stale: 'DEALER_STALE',
} as const;

export const DEALER_CREATED_TOAST = 'Dealer has been added successfully.';
export const DEALER_GENERIC_ERROR = 'Something went wrong. Please try again.';
export const DEALER_UPDATED_TOAST = 'Dealer has been updated successfully.';
/** `DEALER_STALE` and `DEALER_NOT_FOUND` share it (BE §1.6, AC 118643). */
export const DEALER_EDIT_UNAVAILABLE = 'This dealer is no longer available.';

/** Who may act on a Dealer (grilling Q3): gates the More menu (05) and Add Dealer (07). The role,
 *  not `meta.permissions.canManage`, decides — the CTA renders before any response. */
export const DEALER_MANAGE_ROLES: Role[] = ['super_admin', 'admin'];

/** 118479 status-change copy, verbatim (grilling Q4/locked 15). */
export const DEALER_STATUS_COPY = {
  disable: {
    title: 'Disable dealer?',
    subtitle: 'The dealer account will be deactivated and not available for use.',
    confirmText: 'Disable',
    success: 'Dealer has been disabled successfully.',
  },
  enable: {
    title: 'Enable dealer?',
    subtitle: 'The dealer account will be activated and available for use.',
    confirmText: 'Enable',
    success: 'Dealer has been enabled successfully.',
  },
} as const;
/** Any More-menu failure, whatever the code (118479 "A More-menu action fails"). */
export const DEALER_ACTION_ERROR = 'Something went wrong. Please try again.';

/** 118479 confirm + 122593 blocked pop-up, AC copy (grilling Q6/locked 15). Bodies: `decideDealerDelete`. */
export const DEALER_DELETE_COPY = {
  title: 'Delete dealer?',
  body: 'The dealer account will be removed. Their customers will keep their accounts and can continue to use them.',
  confirmText: 'Delete',
  success: 'Dealer deleted.',
  blockedTitle: "Can't delete this dealer",
  blockedCloseText: 'Close',
} as const;
/** The delete race: the server re-counted and refused (BE L589-632). */
export const DEALER_HAS_UNASSIGNED_DEVICES = 'DEALER_HAS_UNASSIGNED_DEVICES';
/** Filter-option and chip labels, in the AC's order (118479 §Filter). Same words as `UserStatusChip`. */
export const DEALER_STATUS_LABEL: Record<DealerStatus, string> = {
  pending_activation: 'Activation Pending',
  active: 'Active',
  inactive: 'Inactive',
};
/** 118479 §B: search or filters match nothing. */
export const DEALERS_NO_MATCH = 'No dealers found.';
